import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import authService from "@/services/authService";
import workspaceService from "@/services/workspaceService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import UserMenu from "@/components/shared/UserMenu";
import { toast } from "sonner";
import {
  ChevronDown,
  Download,
  FileText,
  Filter,
  Folder,
  FolderPlus,
  Grid2X2,
  HelpCircle,
  Image as ImageIcon,
  Link2,
  List,
  MoreVertical,
  Music2,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  Type,
  Upload,
  Users,
  Video,
} from "lucide-react";

const folderSections = [
  { key: "my", label: "My Folders", icon: Folder },
  { key: "shared", label: "Shared with me", icon: Users },
  { key: "starred", label: "Starred", icon: Star },
  { key: "recent", label: "Recent", icon: Search },
  { key: "trash", label: "Trash", icon: Trash2 },
];

const contentTypes = [
  {
    key: "video",
    label: "Videos",
    addLabel: "Add Video",
    singular: "Video",
    icon: Video,
    accent: "text-[#c98905]",
    chip: "bg-[#fff3dc] text-[#b77900]",
    accept: "video/*",
    description: "Upload video files",
  },
  {
    key: "audio",
    label: "Audio",
    addLabel: "Add Audio",
    singular: "Audio",
    icon: Music2,
    accent: "text-emerald-500",
    chip: "bg-emerald-50 text-emerald-600",
    accept: "audio/*",
    description: "Upload audio files",
  },
  {
    key: "text",
    label: "Texts",
    addLabel: "Add Text",
    singular: "Text",
    icon: Type,
    accent: "text-[#d68c12]",
    chip: "bg-[#fff5e5] text-[#c27a00]",
    accept: "",
    description: "Add text content",
  },
  {
    key: "document",
    label: "Documents",
    addLabel: "Add Document",
    singular: "Document",
    icon: FileText,
    accent: "text-red-500",
    chip: "bg-red-50 text-red-600",
    accept: ".pdf,.doc,.docx,.txt,.epub,.mobi,.rtf",
    description: "Upload documents",
  },
  {
    key: "image",
    label: "Images",
    addLabel: "Add Image",
    singular: "Image",
    icon: ImageIcon,
    accent: "text-sky-500",
    chip: "bg-sky-50 text-sky-600",
    accept: "image/*",
    description: "Upload images",
  },
  {
    key: "web_link",
    label: "Web Links",
    addLabel: "Add Web Link",
    singular: "Web Link",
    icon: Link2,
    accent: "text-cyan-500",
    chip: "bg-cyan-50 text-cyan-600",
    accept: "",
    description: "Add web links",
  },
];

const sortOptions = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name" },
];

function formatBytes(bytes = 0) {
  if (!bytes) {
    return "-";
  }

  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function WorkHeader({ user }) {
  const navigate = useNavigate();
  const displayName = user?.name || user?.email?.split("@")[0] || "User";

  const navItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "Participate", path: "/participate" },
    { label: "Publisher", path: "/publisher/workflow" },
    { label: "Narrator", path: "/narrator" },
    { label: "Work", path: "/work", active: true },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-[#eadfcd] bg-white/95 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95">
      <div className="mx-auto flex max-w-[1800px] items-center justify-between gap-6 px-5 py-4 md:px-8">
        <div className="flex items-center gap-8 lg:gap-12">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="flex items-center text-left"
          >
            <img
              src="/images/golden-records-real-logo.png"
              alt="Golden Records"
              className="h-16 w-56 rounded-lg object-cover object-center"
            />
          </button>

          <nav className="hidden items-center gap-8 lg:flex">
            {navItems.map((item) => (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                className={`relative py-2 text-lg font-medium transition-colors ${
                  item.active
                    ? "text-[#9a6500] dark:text-[#f2c95d]"
                    : "text-slate-900 hover:text-[#9a6500] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
                }`}
              >
                {item.label}
                {item.active && (
                  <span className="absolute inset-x-0 -bottom-1 h-[2px] rounded-full bg-[#c98905]" />
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative hidden w-[390px] xl:block">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#c98905]" />
            <Input
              type="text"
              readOnly
              placeholder="Search books, projects, narrators..."
              className="h-12 rounded-2xl border-[#eadfcd] bg-white pl-12 text-sm shadow-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
            />
          </div>

          <ThemeToggle />
          <InboxBell />

          <UserMenu user={user} displayName={displayName} />

        </div>
      </div>
    </header>
  );
}

function EmptyFolderState({ onCreate }) {
  return (
    <div className="rounded-[28px] border border-dashed border-[#e7c98a] bg-white/90 p-12 text-center shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/90 dark:shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
        <FolderPlus className="h-8 w-8" />
      </div>
      <h3
        className="mt-5 text-3xl font-semibold text-slate-950 dark:text-[#f8efe0]"
        style={{ fontFamily: "Playfair Display, serif" }}
      >
        No folders yet
      </h3>
      <p className="mx-auto mt-3 max-w-xl text-base text-slate-600 dark:text-[#cdbf9f]">
        Create your first private workspace folder, then start adding videos, audio,
        text, documents, images, and web links.
      </p>
      <Button
        type="button"
        onClick={onCreate}
        className="mt-7 h-12 rounded-xl bg-[#c98905] px-6 text-base font-semibold text-white hover:bg-[#a96d00]"
      >
        <Plus className="mr-2 h-5 w-5" />
        Create Folder
      </Button>
    </div>
  );
}

export default function Work() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeSection, setActiveSection] = useState("my");
  const [folderView, setFolderView] = useState("grid");
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [activeType, setActiveType] = useState("video");
  const [itemSearch, setItemSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState("");
  const [renameFolderName, setRenameFolderName] = useState("");
  const [folderToRename, setFolderToRename] = useState(null);
  const [contentSheetOpen, setContentSheetOpen] = useState(false);
  const [contentType, setContentType] = useState("video");
  const [selectedFile, setSelectedFile] = useState(null);
  const [textTitle, setTextTitle] = useState("");
  const [textContent, setTextContent] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const normalizedUser = useMemo(
    () => (user ? workspaceService.normalizeUser(user) : null),
    [user]
  );

  const refreshFolders = async () => {
    try {
      const nextFolders = await workspaceService.getFolders();
      setFolders(nextFolders);
      return nextFolders;
    } catch (error) {
      console.error("Failed to load workspaces:", error);
      toast.error(error?.response?.data?.detail || "Failed to load folders");
      return [];
    }
  };

  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      try {
        if (!authService.isAuthenticated()) {
          navigate("/login");
          return;
        }

        const userData = await authService.getMe();
        if (!active) {
          return;
        }

        setUser(userData);
        await refreshFolders();
      } catch (error) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
      }
    };

    loadUser();
    return () => {
      active = false;
    };
  }, [navigate]);

  const visibleFolders = useMemo(() => {
    if (!normalizedUser) {
      return [];
    }

    let nextFolders = folders.filter((folder) => folder.ownerId === normalizedUser.id);

    if (activeSection === "shared") {
      return [];
    }

    if (activeSection === "trash") {
      return nextFolders.filter((folder) => folder.deletedAt);
    }

    nextFolders = nextFolders.filter((folder) => !folder.deletedAt);

    if (activeSection === "starred") {
      return nextFolders.filter((folder) => folder.isStarred);
    }

    if (activeSection === "recent") {
      return [...nextFolders].sort(
        (left, right) => new Date(right.updatedAt) - new Date(left.updatedAt)
      );
    }

    return nextFolders;
  }, [activeSection, folders, normalizedUser]);

  useEffect(() => {
    if (!visibleFolders.length) {
      setSelectedFolderId(null);
      return;
    }

    const selectedStillVisible = visibleFolders.some((folder) => folder.id === selectedFolderId);
    if (!selectedStillVisible) {
      setSelectedFolderId(visibleFolders[0].id);
    }
  }, [selectedFolderId, visibleFolders]);

  const selectedFolder =
    visibleFolders.find((folder) => folder.id === selectedFolderId) || null;
  const selectedFolderIsOwner =
    !!selectedFolder && selectedFolder.ownerId === normalizedUser?.id;

  const typeMeta = contentTypes.find((item) => item.key === activeType) || contentTypes[0];

  const filteredItems = useMemo(() => {
    if (!selectedFolder) {
      return [];
    }

    let items = (selectedFolder.items || []).filter((item) => item.type === activeType);

    if (itemSearch.trim()) {
      const query = itemSearch.trim().toLowerCase();
      items = items.filter((item) => {
        const haystack = [
          item.name,
          item.textContent,
          item.url,
          item.ownerName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    if (sortBy === "name") {
      items = [...items].sort((left, right) => left.name.localeCompare(right.name));
    } else if (sortBy === "oldest") {
      items = [...items].sort(
        (left, right) => new Date(left.createdAt) - new Date(right.createdAt)
      );
    } else {
      items = [...items].sort(
        (left, right) => new Date(right.createdAt) - new Date(left.createdAt)
      );
    }

    return items;
  }, [activeType, itemSearch, selectedFolder, sortBy]);

  const storageSummary = useMemo(() => {
    const visibleBytes = visibleFolders.reduce((total, folder) => {
      return (
        total +
        (folder.items || []).reduce((folderTotal, item) => folderTotal + (Number(item.size) || 0), 0)
      );
    }, 0);

    return {
      usedBytes: visibleBytes,
      usedLabel: formatBytes(visibleBytes),
    };
  }, [visibleFolders]);

  const countsByType = useMemo(() => {
    const base = {};
    contentTypes.forEach((type) => {
      base[type.key] = 0;
    });

    if (!selectedFolder) {
      return base;
    }

    (selectedFolder.items || []).forEach((item) => {
      if (Object.prototype.hasOwnProperty.call(base, item.type)) {
        base[item.type] += 1;
      }
    });

    return base;
  }, [selectedFolder]);

  const resetContentForm = () => {
    setSelectedFile(null);
    setTextTitle("");
    setTextContent("");
    setLinkTitle("");
    setLinkUrl("");
  };

  const openCreateDialog = () => {
    setCreateFolderName("");
    setCreateDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    const name = createFolderName.trim();
    if (!name || !normalizedUser) {
      return;
    }

    try {
      const folder = await workspaceService.createFolder(normalizedUser, name);
      await refreshFolders();
      setSelectedFolderId(folder.id);
      setCreateDialogOpen(false);
      toast.success("Folder created");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to create folder");
    }
  };

  const openRenameDialog = (folder) => {
    setFolderToRename(folder);
    setRenameFolderName(folder.name);
    setRenameDialogOpen(true);
  };

  const handleRenameFolder = async () => {
    const name = renameFolderName.trim();
    if (!folderToRename || !name) {
      return;
    }

    try {
      await workspaceService.renameFolder(folderToRename.id, name);
      await refreshFolders();
      setRenameDialogOpen(false);
      setFolderToRename(null);
      toast.success("Folder updated");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to rename folder");
    }
  };

  const toggleFolderStar = async (folder) => {
    try {
      await workspaceService.toggleFolderStar(folder.id, !folder.isStarred);
      await refreshFolders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update folder");
    }
  };

  const moveFolderToTrash = async (folder) => {
    try {
      await workspaceService.moveFolderToTrash(folder.id);
      await refreshFolders();
      toast.success("Folder moved to trash");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to move folder");
    }
  };

  const restoreFolder = async (folder) => {
    try {
      await workspaceService.restoreFolder(folder.id);
      await refreshFolders();
      toast.success("Folder restored");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to restore folder");
    }
  };

  const deleteFolderForever = async (folder) => {
    try {
      await workspaceService.deleteFolderPermanently(folder.id);
      await refreshFolders();
      toast.success("Folder deleted");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete folder");
    }
  };

  const openContentSheet = (type = "video") => {
    setContentType(type);
    resetContentForm();
    setContentSheetOpen(true);
  };

  const handleFileSelected = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleAddContent = async () => {
    if (!selectedFolder || !selectedFolderIsOwner) {
      return;
    }

    const currentType = contentType;

    try {
      if (["video", "audio", "document", "image"].includes(currentType)) {
        if (!selectedFile) {
          toast.error("Choose a file first");
          return;
        }

        await workspaceService.uploadFileItem(selectedFolder.id, currentType, selectedFile);
      }

      if (currentType === "text") {
        if (!textContent.trim()) {
          toast.error("Add some text content first");
          return;
        }

        await workspaceService.addTextItem(selectedFolder.id, {
          name: textTitle.trim() || `Text note ${formatDate(new Date().toISOString())}`,
          textContent: textContent.trim(),
        });
      }

      if (currentType === "web_link") {
        if (!linkUrl.trim()) {
          toast.error("Add a web link first");
          return;
        }

        await workspaceService.addLinkItem(selectedFolder.id, {
          name: linkTitle.trim() || linkUrl.trim(),
          url: linkUrl.trim(),
        });
      }

      await refreshFolders();
      setContentSheetOpen(false);
      resetContentForm();
      setActiveType(currentType);
      toast.success(`${contentTypes.find((item) => item.key === currentType)?.singular || "Content"} added`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to add content");
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!selectedFolder || !selectedFolderIsOwner) {
      return;
    }

    try {
      await workspaceService.removeItem(itemId);
      await refreshFolders();
      toast.success("Content removed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to remove content");
    }
  };

  const activeAddMeta =
    contentTypes.find((item) => item.key === contentType) || contentTypes[0];
  const EmptyTypeIcon = typeMeta.icon;

  return (
    <div className="min-h-screen bg-[#fffaf2] text-slate-900 dark:bg-[#050504] dark:text-[#f8efe0]">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_85%_10%,rgba(245,207,117,0.18),transparent_22%),linear-gradient(180deg,#ffffff_0%,#fffaf2_45%,#fff2dc_100%)] dark:bg-[radial-gradient(circle_at_82%_10%,rgba(224,165,27,0.26),transparent_20%),linear-gradient(180deg,#050504_0%,#090805_55%,#030302_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[56vh] opacity-60 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.16)_0_1px,transparent_1px_17px)] dark:opacity-30" />

      <WorkHeader user={normalizedUser} />

      <main className="mx-auto max-w-[1800px] px-4 py-6 md:px-8">
        <div className="grid gap-6 xl:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-6">
            <div className="rounded-[26px] border border-[#eadfcd] bg-white/92 p-4 shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/92 dark:shadow-[0_18px_55px_rgba(0,0,0,0.38)]">
              <div className="space-y-2">
                {folderSections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.key;
                  return (
                    <button
                      key={section.key}
                      type="button"
                      onClick={() => setActiveSection(section.key)}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-4 text-left text-base font-medium transition ${
                        isActive
                          ? "bg-[#fff6e7] text-[#b77900] dark:bg-[#1c1509] dark:text-[#f2c95d]"
                          : "text-slate-700 hover:bg-[#fffaf2] dark:text-[#ddd0bb] dark:hover:bg-[#161109]"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[26px] border border-[#eadfcd] bg-white/92 p-5 shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/92 dark:shadow-[0_18px_55px_rgba(0,0,0,0.38)]">
              <h3 className="text-xl font-semibold text-slate-950 dark:text-[#f8efe0]">Storage</h3>
              <p className="mt-3 text-sm font-medium text-slate-700 dark:text-[#ddd0bb]">
                {storageSummary.usedLabel} used
              </p>
              <div className="mt-4 h-2 rounded-full bg-[#f4ead6] dark:bg-[#22190c]">
                <div
                  className="h-2 rounded-full bg-[#c98905]"
                  style={{ width: `${Math.min((storageSummary.usedBytes / (50 * 1024 * 1024 * 1024)) * 100, 100)}%` }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="mt-5 h-11 w-full rounded-xl border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
              >
                Upgrade Storage
              </Button>
            </div>

            <div className="rounded-[26px] border border-[#eadfcd] bg-white/92 p-5 shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/92 dark:shadow-[0_18px_55px_rgba(0,0,0,0.38)]">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                  <HelpCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Need Help?</h3>
                  <p className="text-sm text-slate-600 dark:text-[#cdbf9f]">
                    Our support team is here to help you.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="mt-5 h-11 w-full rounded-xl border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
              >
                Contact Support
              </Button>
            </div>
          </aside>

          <section className="space-y-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h1
                  className="text-5xl font-semibold text-slate-950 dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Work
                </h1>
                <p className="mt-3 text-lg text-slate-600 dark:text-[#cdbf9f]">
                  Create folders and organize your content
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={openCreateDialog}
                  className="h-12 rounded-xl bg-[#c98905] px-5 text-base font-semibold text-white hover:bg-[#a96d00]"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Create Folder
                </Button>

                <div className="inline-flex overflow-hidden rounded-xl border border-[#eadfcd] bg-white shadow-sm dark:border-[#4d3d16] dark:bg-[#11100d]">
                  <button
                    type="button"
                    onClick={() => setFolderView("grid")}
                    className={`grid h-12 w-12 place-items-center ${
                      folderView === "grid"
                        ? "bg-[#fff6e7] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]"
                        : "text-slate-500 dark:text-[#cdbf9f]"
                    }`}
                  >
                    <Grid2X2 className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setFolderView("list")}
                    className={`grid h-12 w-12 place-items-center border-l border-[#eadfcd] dark:border-[#4d3d16] ${
                      folderView === "list"
                        ? "bg-[#fff6e7] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]"
                        : "text-slate-500 dark:text-[#cdbf9f]"
                    }`}
                  >
                    <List className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {activeSection === "shared" ? (
              <div className="rounded-[28px] border border-[#eadfcd] bg-white/92 p-12 text-center shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/92 dark:shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
                <Users className="mx-auto h-12 w-12 text-[#c98905]" />
                <h3
                  className="mt-5 text-3xl font-semibold text-slate-950 dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Shared workspace is ready for later
                </h3>
                <p className="mx-auto mt-3 max-w-xl text-base text-slate-600 dark:text-[#cdbf9f]">
                  Right now each folder is private to its owner. Admin can inspect all folders,
                  and we can add true sharing next.
                </p>
              </div>
            ) : !visibleFolders.length && activeSection !== "trash" ? (
              <EmptyFolderState onCreate={openCreateDialog} />
            ) : (
              <>
                <div
                  className={
                    folderView === "grid"
                      ? "grid gap-4 md:grid-cols-2 2xl:grid-cols-5"
                      : "space-y-3"
                  }
                >
                  <button
                    type="button"
                    onClick={openCreateDialog}
                    className={`rounded-[26px] border border-dashed border-[#e7c98a] bg-white/88 p-6 text-left shadow-[0_18px_55px_rgba(111,79,18,0.08)] transition hover:border-[#c98905] hover:shadow-[0_24px_65px_rgba(111,79,18,0.12)] dark:border-[#4d3d16] dark:bg-[#100d08]/88 dark:shadow-[0_18px_55px_rgba(0,0,0,0.35)] ${
                      folderView === "list" ? "flex items-center gap-4" : ""
                    }`}
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                      <Plus className="h-6 w-6" />
                    </div>
                    <div className="mt-4 text-lg font-semibold text-[#b77900] dark:text-[#f2c95d]">
                      Create New Folder
                    </div>
                  </button>

                  {visibleFolders.map((folder) => {
                    const count = (folder.items || []).length;
                    const canManage = folder.ownerId === normalizedUser?.id;

                    return (
                      <div
                        key={folder.id}
                        onClick={() => setSelectedFolderId(folder.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedFolderId(folder.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className={`rounded-[26px] border p-6 text-left shadow-[0_18px_55px_rgba(111,79,18,0.08)] transition dark:shadow-[0_18px_55px_rgba(0,0,0,0.35)] ${
                          selectedFolderId === folder.id
                            ? "border-[#d4a63a] bg-[#fffaf2] dark:border-[#8a6a24] dark:bg-[#120f09]"
                            : "border-[#eadfcd] bg-white/92 hover:border-[#d4a63a] dark:border-[#4d3d16] dark:bg-[#100d08]/92"
                        } ${folderView === "list" ? "flex items-center justify-between" : ""}`}
                      >
                        <div className={folderView === "list" ? "flex items-center gap-4" : ""}>
                          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                            <Folder className="h-7 w-7" />
                          </div>
                          <div className={folderView === "grid" ? "mt-5" : ""}>
                            <div className="flex items-center gap-2">
                              <h3 className="text-2xl font-semibold text-slate-950 dark:text-[#f8efe0]">
                                {folder.name}
                              </h3>
                              {folder.isStarred && (
                                <Star className="h-4 w-4 fill-[#d4a63a] text-[#d4a63a]" />
                              )}
                            </div>
                            <p className="mt-2 text-sm text-slate-600 dark:text-[#cdbf9f]">
                              {count} {count === 1 ? "item" : "items"}
                            </p>
                          </div>
                        </div>

                        <div onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="rounded-lg p-2 text-slate-500 hover:bg-[#fff6e7] hover:text-[#b77900] dark:text-[#cdbf9f] dark:hover:bg-[#1b1408] dark:hover:text-[#f2c95d]"
                              >
                                <MoreVertical className="h-5 w-5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-48 border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
                              {canManage && (
                                <DropdownMenuItem onClick={() => openRenameDialog(folder)}>
                                  <Pencil className="h-4 w-4" />
                                  Rename
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => toggleFolderStar(folder)}>
                                <Star className="h-4 w-4" />
                                {folder.isStarred ? "Remove star" : "Add star"}
                              </DropdownMenuItem>
                              {folder.deletedAt ? (
                                <>
                                  <DropdownMenuItem onClick={() => restoreFolder(folder)}>
                                    <Upload className="h-4 w-4" />
                                    Restore
                                  </DropdownMenuItem>
                                  {canManage && (
                                    <DropdownMenuItem
                                      onClick={() => deleteFolderForever(folder)}
                                      className="text-red-600 focus:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Delete forever
                                    </DropdownMenuItem>
                                  )}
                                </>
                              ) : (
                                canManage && (
                                  <DropdownMenuItem onClick={() => moveFolderToTrash(folder)}>
                                    <Trash2 className="h-4 w-4" />
                                    Move to trash
                                  </DropdownMenuItem>
                                )
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {activeSection === "trash" ? null : selectedFolder && (
                  <div className="rounded-[28px] border border-[#eadfcd] bg-white/94 shadow-[0_18px_55px_rgba(111,79,18,0.08)] dark:border-[#4d3d16] dark:bg-[#100d08]/94 dark:shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
                    <div className="border-b border-[#f0e2ca] px-6 py-6 dark:border-[#3a2d13]">
                      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-500 dark:text-[#cdbf9f]">
                            My Folders &nbsp; &gt; &nbsp; {selectedFolder.name}
                          </div>
                          <div className="mt-3 flex items-center gap-4">
                            <div className="grid h-16 w-16 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                              <Folder className="h-8 w-8" />
                            </div>
                            <div>
                              <h2
                                className="text-4xl font-semibold text-slate-950 dark:text-[#f8efe0]"
                                style={{ fontFamily: "Playfair Display, serif" }}
                              >
                                {selectedFolder.name}
                              </h2>
                              <p className="mt-1 text-base text-slate-600 dark:text-[#cdbf9f]">
                                {(selectedFolder.items || []).length} items • Created on{" "}
                                {formatDate(selectedFolder.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                disabled={!selectedFolderIsOwner}
                                className="h-12 rounded-xl bg-[#c98905] px-5 text-base font-semibold text-white hover:bg-[#a96d00] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="mr-2 h-5 w-5" />
                                Add Content
                                <ChevronDown className="ml-2 h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-56 border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
                              {contentTypes.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <DropdownMenuItem
                                    key={item.key}
                                    onClick={() => openContentSheet(item.key)}
                                  >
                                    <Icon className={`h-4 w-4 ${item.accent}`} />
                                    {item.addLabel}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          <div className="inline-flex overflow-hidden rounded-xl border border-[#eadfcd] bg-white shadow-sm dark:border-[#4d3d16] dark:bg-[#11100d]">
                            <button
                              type="button"
                              onClick={() => setFolderView("grid")}
                              className="grid h-12 w-12 place-items-center text-[#c98905]"
                            >
                              <Grid2X2 className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setFolderView("list")}
                              className="grid h-12 w-12 place-items-center border-l border-[#eadfcd] text-slate-500 dark:border-[#4d3d16] dark:text-[#cdbf9f]"
                            >
                              <List className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-b border-[#f0e2ca] px-6 py-4 dark:border-[#3a2d13]">
                      <div className="flex flex-wrap items-center gap-3">
                        {contentTypes.map((item) => {
                          const Icon = item.icon;
                          const isActive = activeType === item.key;
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => setActiveType(item.key)}
                              className={`inline-flex items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold transition ${
                                isActive
                                  ? "bg-[#fff6e7] text-[#b77900] shadow-sm dark:bg-[#1b1408] dark:text-[#f2c95d]"
                                  : "text-slate-700 hover:bg-[#fffaf2] dark:text-[#ddd0bb] dark:hover:bg-[#161109]"
                              }`}
                            >
                              <Icon className={`h-5 w-5 ${item.accent}`} />
                              {item.label}
                              <span className="rounded-full bg-[#fff4e1] px-2 py-0.5 text-xs font-bold text-[#b77900] dark:bg-[#251b08] dark:text-[#f2c95d]">
                                {countsByType[item.key]}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="px-6 py-6">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <h3
                            className="text-3xl font-semibold text-slate-950 dark:text-[#f8efe0]"
                            style={{ fontFamily: "Playfair Display, serif" }}
                          >
                            {typeMeta.label}
                          </h3>
                          <p className="mt-1 text-base text-slate-600 dark:text-[#cdbf9f]">
                            All your {typeMeta.label.toLowerCase()} in this folder
                          </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row">
                          <div className="relative w-full sm:w-[330px]">
                            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                            <Input
                              value={itemSearch}
                              onChange={(event) => setItemSearch(event.target.value)}
                              placeholder={`Search ${typeMeta.label.toLowerCase()}...`}
                              className="h-12 rounded-xl border-[#eadfcd] bg-white pl-12 text-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
                            />
                          </div>

                          <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="h-12 w-full rounded-xl border-[#eadfcd] bg-white text-slate-900 sm:w-[180px] dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
                              <Filter className="mr-2 h-4 w-4 text-[#c98905]" />
                              <SelectValue placeholder="Sort" />
                            </SelectTrigger>
                            <SelectContent className="border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
                              {sortOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mt-6 overflow-hidden rounded-2xl border border-[#f0e2ca] dark:border-[#3a2d13]">
                        {filteredItems.length > 0 ? (
                          <Table>
                            <TableHeader className="bg-[#fffaf2] dark:bg-[#151109]">
                              <TableRow className="border-[#f0e2ca] dark:border-[#3a2d13] hover:bg-transparent">
                                <TableHead className="px-5 py-4 text-sm font-semibold text-slate-600 dark:text-[#cdbf9f]">
                                  Name
                                </TableHead>
                                <TableHead className="px-5 py-4 text-sm font-semibold text-slate-600 dark:text-[#cdbf9f]">
                                  Size
                                </TableHead>
                                <TableHead className="px-5 py-4 text-sm font-semibold text-slate-600 dark:text-[#cdbf9f]">
                                  Added On
                                </TableHead>
                                <TableHead className="px-5 py-4 text-right text-sm font-semibold text-slate-600 dark:text-[#cdbf9f]">
                                  Actions
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredItems.map((item) => {
                                const Icon = typeMeta.icon;
                                return (
                                  <TableRow
                                    key={item.id}
                                    className="border-[#f0e2ca] dark:border-[#3a2d13] dark:hover:bg-[#151109]"
                                  >
                                    <TableCell className="px-5 py-4">
                                      <div className="flex items-center gap-4">
                                        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                                          <Icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                          <div className="text-base font-semibold text-slate-950 dark:text-[#f8efe0]">
                                            {item.name}
                                          </div>
                                          {item.url && (
                                            <div className="mt-1 text-xs text-slate-500 dark:text-[#a89779]">
                                              {item.url}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-sm text-slate-600 dark:text-[#cdbf9f]">
                                      {formatBytes(item.size)}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-sm text-slate-600 dark:text-[#cdbf9f]">
                                      {formatDate(item.createdAt)}
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                      <div className="flex items-center justify-end gap-2">
                                        {item.url ? (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => workspaceService.downloadItem(item)}
                                            className="h-9 rounded-lg border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
                                          >
                                            Open
                                          </Button>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => workspaceService.downloadItem(item)}
                                            className="rounded-lg p-2 text-slate-500 hover:bg-[#fff6e7] hover:text-[#b77900] dark:text-[#cdbf9f] dark:hover:bg-[#1b1408] dark:hover:text-[#f2c95d]"
                                          >
                                            <Download className="h-4 w-4" />
                                          </button>
                                        )}
                                        {selectedFolderIsOwner && (
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <button
                                                type="button"
                                                className="rounded-lg p-2 text-slate-500 hover:bg-[#fff6e7] hover:text-[#b77900] dark:text-[#cdbf9f] dark:hover:bg-[#1b1408] dark:hover:text-[#f2c95d]"
                                              >
                                                <MoreVertical className="h-4 w-4" />
                                              </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="w-44 border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
                                              <DropdownMenuItem onClick={() => handleRemoveItem(item.id)}>
                                                <Trash2 className="h-4 w-4" />
                                                Remove
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="px-6 py-16 text-center">
                            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                              <EmptyTypeIcon className="h-8 w-8" />
                            </div>
                            <h4 className="mt-6 text-3xl font-semibold text-slate-950 dark:text-[#f8efe0]">
                              No {typeMeta.label.toLowerCase()} yet
                            </h4>
                            <p className="mx-auto mt-3 max-w-xl text-base text-slate-600 dark:text-[#cdbf9f]">
                              {selectedFolderIsOwner
                                ? `Use Add Content to place your ${typeMeta.label.toLowerCase()} inside this folder.`
                                : `This user has not added any ${typeMeta.label.toLowerCase()} here yet.`}
                            </p>
                            {selectedFolderIsOwner && (
                              <Button
                                type="button"
                                onClick={() => openContentSheet(activeType)}
                                className="mt-6 h-11 rounded-xl bg-[#c98905] px-5 text-base font-semibold text-white hover:bg-[#a96d00]"
                              >
                                <Plus className="mr-2 h-5 w-5" />
                                {typeMeta.addLabel}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="border-[#eadfcd] bg-white sm:max-w-[520px] dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Create Folder
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-[#cdbf9f]">
              Give your workspace folder a clear name so you can organize content easily.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              value={createFolderName}
              onChange={(event) => setCreateFolderName(event.target.value)}
              placeholder="My Book"
              className="h-12 rounded-xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateFolder}
              disabled={!createFolderName.trim()}
              className="bg-[#c98905] text-white hover:bg-[#a96d00] disabled:opacity-50"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="border-[#eadfcd] bg-white sm:max-w-[520px] dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Rename Folder
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-[#cdbf9f]">
              Update your folder name without changing any content inside it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="rename-folder-name">Folder name</Label>
            <Input
              id="rename-folder-name"
              value={renameFolderName}
              onChange={(event) => setRenameFolderName(event.target.value)}
              className="h-12 rounded-xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameDialogOpen(false)}
              className="border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRenameFolder}
              disabled={!renameFolderName.trim()}
              className="bg-[#c98905] text-white hover:bg-[#a96d00] disabled:opacity-50"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={contentSheetOpen} onOpenChange={setContentSheetOpen}>
        <SheetContent
          side="right"
          className="flex h-screen w-full max-w-[560px] flex-col overflow-hidden border-l border-[#eadfcd] bg-white px-6 py-6 dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] sm:max-w-[560px]"
        >
          <div className="flex-1 overflow-y-auto pr-1">
            <SheetHeader className="space-y-2 text-left">
              <SheetTitle
                className="text-3xl text-slate-950 dark:text-[#f8efe0]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Add Content
              </SheetTitle>
              <SheetDescription className="text-slate-600 dark:text-[#cdbf9f]">
                Select the kind of content you want to add to {selectedFolder?.name || "this folder"}.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {contentTypes.map((item) => {
                const Icon = item.icon;
                const isActive = contentType === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setContentType(item.key);
                      resetContentForm();
                    }}
                    className={`rounded-2xl border p-4 text-left transition ${
                      isActive
                        ? "border-[#d4a63a] bg-[#fffaf2] shadow-sm dark:border-[#8a6a24] dark:bg-[#191309]"
                        : "border-[#eadfcd] bg-white hover:border-[#d4a63a] dark:border-[#4d3d16] dark:bg-[#0f0d08]"
                    }`}
                  >
                    <div className={`grid h-12 w-12 place-items-center rounded-2xl ${item.chip}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="mt-4 text-xl font-semibold text-slate-950 dark:text-[#f8efe0]">
                      {item.singular}
                    </div>
                    <div className="mt-1 text-sm text-slate-600 dark:text-[#cdbf9f]">
                      {item.description}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 rounded-[24px] border border-[#eadfcd] bg-[#fffdf9] p-5 dark:border-[#4d3d16] dark:bg-[#120f09]">
              <div className="mb-4 text-lg font-semibold text-slate-950 dark:text-[#f8efe0]">
                {activeAddMeta.addLabel}
              </div>

              {["video", "audio", "document", "image"].includes(contentType) && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-dashed border-[#d8bd7a] bg-white p-6 text-center dark:border-[#8a6a24] dark:bg-[#0f0d08]">
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#fff4e1] text-[#c98905] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                      <Upload className="h-6 w-6" />
                    </div>
                    <p className="mt-4 text-base font-medium text-slate-700 dark:text-[#ddd0bb]">
                      Choose a {activeAddMeta.singular.toLowerCase()} file to attach
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={activeAddMeta.accept}
                      onChange={handleFileSelected}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-4 border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
                    >
                      Browse Files
                    </Button>
                    {selectedFile && (
                      <div className="mt-4 rounded-xl bg-[#fff6e7] px-4 py-3 text-left text-sm text-[#8a5b00] dark:bg-[#1b1408] dark:text-[#f2c95d]">
                        <div className="font-semibold">{selectedFile.name}</div>
                        <div>{formatBytes(selectedFile.size)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {contentType === "text" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="text-title">Title</Label>
                    <Input
                      id="text-title"
                      value={textTitle}
                      onChange={(event) => setTextTitle(event.target.value)}
                      placeholder="Chapter notes"
                      className="h-12 rounded-xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="text-content">Text content</Label>
                    <Textarea
                      id="text-content"
                      value={textContent}
                      onChange={(event) => setTextContent(event.target.value)}
                      placeholder="Add your notes, draft, transcript, or anything else here..."
                      className="min-h-[180px] rounded-2xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
                    />
                  </div>
                </div>
              )}

              {contentType === "web_link" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="link-title">Title</Label>
                    <Input
                      id="link-title"
                      value={linkTitle}
                      onChange={(event) => setLinkTitle(event.target.value)}
                      placeholder="Reference article"
                      className="h-12 rounded-xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="link-url">Web link</Label>
                    <Input
                      id="link-url"
                      value={linkUrl}
                      onChange={(event) => setLinkUrl(event.target.value)}
                      placeholder="https://example.com"
                      className="h-12 rounded-xl border-[#eadfcd] bg-white dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-[#eadfcd] bg-white pt-4 dark:border-[#3a2d13] dark:bg-[#11100d]">
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleAddContent}
                className="h-12 rounded-xl bg-[#c98905] px-6 text-base font-semibold text-white hover:bg-[#a96d00]"
              >
                <Plus className="mr-2 h-5 w-5" />
                Submit {activeAddMeta.singular}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
